import { useState, useEffect, useRef } from "react";
import { T } from "../../config/theme.js";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";

const API_BASE = import.meta.env.VITE_API_URL || "";

export default function SupportChatbot() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([
    { role: "bot", text: "안녕하세요. Pause 고객 지원입니다. 무엇을 도와드릴까요?" }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const bottom = useRef(null);

  // ── 드래그 이동 상태 ──
  const [pos, setPos] = useState({ x: 24, y: 24 }); // right, bottom 기준
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef(null);
  const posStart = useRef(null);
  const hasMoved = useRef(false);
  const btnRef = useRef(null);

  useEffect(() => { bottom.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  // ── 드래그 핸들러 (터치 + 마우스) ──
  const onDragStart = (e) => {
    const point = e.touches ? e.touches[0] : e;
    dragStart.current = { x: point.clientX, y: point.clientY };
    posStart.current = { ...pos };
    hasMoved.current = false;
    setDragging(true);
  };

  useEffect(() => {
    if (!dragging) return;

    const onMove = (e) => {
      const point = e.touches ? e.touches[0] : e;
      const dx = point.clientX - dragStart.current.x;
      const dy = point.clientY - dragStart.current.y;

      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
        hasMoved.current = true;
      }

      const newX = Math.max(8, Math.min(window.innerWidth - 64, posStart.current.x - dx));
      const newY = Math.max(8, Math.min(window.innerHeight - 64, posStart.current.y - dy));
      setPos({ x: newX, y: newY });
    };

    const onEnd = () => {
      setDragging(false);
      // 위치 저장
      localStorage.setItem("pause_chatbot_pos", JSON.stringify(pos));
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onEnd);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onEnd);

    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onEnd);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onEnd);
    };
  }, [dragging]);

  // 저장된 위치 복원
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("pause_chatbot_pos"));
      if (saved?.x && saved?.y) setPos(saved);
    } catch {}
  }, []);

  const suggestions = [
    "Pause는 어떤 서비스인가요?",
    "요금제를 알려주세요",
    "종목 추천도 해주나요?",
    "환불이 가능한가요?",
    "어떤 데이터를 분석하나요?",
  ];

  const send = async (text) => {
    const q = (text || input).trim();
    if (!q || loading) return;
    setInput("");

    const userMsg = { role: "user", text: q };
    setMsgs(prev => [...prev, userMsg]);
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/api/v1/support/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: q,
          history: msgs.slice(-10),
        }),
      });

      if (!res.ok) throw new Error("서버 응답 오류");

      const data = await res.json();
      setMsgs(prev => [...prev, { role: "bot", text: data.answer }]);
    } catch (err) {
      setMsgs(prev => [...prev, {
        role: "bot",
        text: "일시적인 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.\n문제가 지속되면 support@pause.ai.kr로 문의해 주세요."
      }]);
    }

    setLoading(false);
  };

  // ── 닫힌 상태 — 드래그 가능 플로팅 버튼 ──
  if (!open) return (
    <div
      ref={btnRef}
      onMouseDown={onDragStart}
      onTouchStart={onDragStart}
      onClick={() => { if (!hasMoved.current) setOpen(true); }}
      style={{
        position: "fixed", bottom: pos.y, right: pos.x,
        width: 52, height: 52,
        borderRadius: "50%", background: T.accent,
        display: "flex", alignItems: "center", justifyContent: "center",
        cursor: dragging ? "grabbing" : "grab",
        boxShadow: `0 4px 20px ${T.accentGlow || "rgba(31,107,92,0.4)"}`,
        transition: dragging ? "none" : "transform 0.2s",
        zIndex: 9999, touchAction: "none", userSelect: "none",
      }}
    >
      <MessageCircle size={22} color="#fff" />
    </div>
  );

  // ── 열린 상태 — 채팅 패널 ──
  return (
    <div style={{
      position: "fixed", bottom: 24, right: 24,
      width: "min(380px, calc(100vw - 32px))", height: "min(520px, calc(100vh - 100px))",
      borderRadius: 20, background: T.card, border: `1px solid ${T.border}`,
      boxShadow: "0 8px 40px rgba(0,0,0,0.3)", display: "flex", flexDirection: "column",
      overflow: "hidden", zIndex: 9999,
    }}>
      {/* Header */}
      <div style={{ padding: "16px 20px", background: T.accent, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#fff" }}>Pause 고객 지원</div>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.7)", marginTop: 2 }}>평일 10:00 ~ 18:00</div>
        </div>
        <X size={20} color="#fff" style={{ cursor: "pointer", opacity: 0.8 }} onClick={() => setOpen(false)} />
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflow: "auto", padding: "16px 14px", display: "flex", flexDirection: "column", gap: 10 }}>
        {msgs.map((m, i) => (
          <div key={i} style={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start" }}>
            <div style={{
              maxWidth: "82%", padding: "10px 14px", borderRadius: 14,
              background: m.role === "user" ? T.accent : T.bg,
              border: m.role === "user" ? "none" : `1px solid ${T.border}`,
              color: m.role === "user" ? "#fff" : T.text,
              fontSize: 13, lineHeight: 1.7, whiteSpace: "pre-line",
            }}>
              {m.text}
            </div>
          </div>
        ))}

        {loading && (
          <div style={{ display: "flex", justifyContent: "flex-start" }}>
            <div style={{ padding: "10px 14px", borderRadius: 14, background: T.bg, border: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 8 }}>
              <Loader2 size={14} color={T.textMuted} style={{ animation: "spin 1s linear infinite" }} />
              <span style={{ fontSize: 13, color: T.textMuted }}>답변을 준비하고 있습니다</span>
            </div>
          </div>
        )}

        {msgs.length === 1 && !loading && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 4 }}>
            {suggestions.map((s, i) => (
              <div key={i} onClick={() => send(s)}
                style={{
                  padding: "6px 12px", background: T.accent + "12", borderRadius: 20,
                  fontSize: 12, color: T.accent, cursor: "pointer",
                  border: `1px solid ${T.accent}25`, transition: "all 0.15s",
                }}
                onMouseEnter={e => { e.currentTarget.style.background = T.accent + "22"; }}
                onMouseLeave={e => { e.currentTarget.style.background = T.accent + "12"; }}
              >
                {s}
              </div>
            ))}
          </div>
        )}
        <div ref={bottom} />
      </div>

      {/* Input */}
      <div style={{ padding: "12px 14px", borderTop: `1px solid ${T.border}`, display: "flex", gap: 8 }}>
        <input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === "Enter" && !e.shiftKey && send()}
          placeholder="질문을 입력해 주세요"
          disabled={loading}
          style={{
            flex: 1, padding: "10px 14px", borderRadius: 10,
            border: `1px solid ${T.border}`, background: T.bg,
            color: T.text, fontSize: 13, outline: "none", fontFamily: "inherit",
            opacity: loading ? 0.5 : 1,
          }}
        />
        <div onClick={() => send()} style={{
          padding: "10px 14px", borderRadius: 10, background: T.accent,
          color: "#fff", cursor: loading ? "not-allowed" : "pointer",
          fontSize: 13, fontWeight: 600, display: "flex", alignItems: "center",
          opacity: loading ? 0.5 : 1,
        }}>
          <Send size={16} />
        </div>
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
